<template>
  <v-container fluid class="pa-2">
    <v-card outlined class="compact-card">
      <v-card-text class="pa-3">
        <v-form>
          <v-row dense>
            <v-col cols="12" sm="5">
              <v-menu
                v-model="startMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="startDate"
                    label="Start Date"
                    prepend-icon="mdi-calendar-start"
                    readonly
                    outlined
                    dense
                    hide-details
                    class="small-text-field"
                    v-bind="attrs"
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="startDate"
                  @input="startMenu = false"
                  :max="endDate"
                ></v-date-picker>
              </v-menu>
            </v-col>
            <v-col cols="12" sm="5">
              <v-menu
                v-model="endMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="endDate"
                    label="End Date"
                    prepend-icon="mdi-calendar-end"
                    readonly
                    outlined
                    dense
                    hide-details
                    class="small-text-field"
                    v-bind="attrs"
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="endDate"
                  @input="endMenu = false"
                  :min="startDate"
                ></v-date-picker>
              </v-menu>
            </v-col>
            <v-col cols="12" sm="2" class="d-flex align-center">
              <v-btn
                color="primary"
                small
                :loading="loading"
                :disabled="loading"
                @click="loadData"
                block
                class="caption"
              >
                <v-icon left x-small>mdi-refresh</v-icon> Generate
              </v-btn>
            </v-col>
          </v-row>
        </v-form>

        <div v-if="loading" class="text-center py-8">
          <v-progress-circular indeterminate color="primary" size="48" />
          <div class="mt-3 caption grey--text">Loading bank received data...</div>
        </div>

        <div v-else-if="!rows.length" class="text-center py-8 grey--text text--darken-1 caption">
          No bank received transactions found for the selected period
        </div>

        <template v-else>
          <v-row class="mt-4 mb-3" align="center">
            <v-col cols="12" md="8">
              <v-row dense>
                <v-col cols="12" sm="6">
                  <v-select
                    v-model="selectedUnit"
                    :items="unitItems"
                    label="Filter by Unit"
                    outlined
                    dense
                    clearable
                    hide-details
                    class="small-select"
                  ></v-select>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-btn-toggle v-model="sortMode" mandatory dense small>
                    <v-btn small value="reference" class="caption">Ref</v-btn>
                    <v-btn small value="unit" class="caption">Unit</v-btn>
                  </v-btn-toggle>
                </v-col>
              </v-row>
            </v-col>
            <v-col cols="12" md="4" class="text-right">
              <v-btn
                color="error"
                dark
                small
                :loading="pdfLoading"
                @click="exportPdf"
                :disabled="!filteredRows.length"
                class="caption"
              >
                <v-icon left small>mdi-file-pdf-box</v-icon>
                Export PDF
              </v-btn>
            </v-col>
          </v-row>

          <!-- Print area -->
          <div id="print-area">
            <div class="text-center mb-4">
              <h5 class="mb-1 font-weight-bold">Bank Received Report</h5>
              <div class="caption grey--text">
                Period: {{ formatDate(startDate) }} — {{ formatDate(endDate) }}
              </div>
            </div>

            <div class="table-responsive">
              <table class="bank-table">
                <thead>
                  <tr>
                    <th class="reference-col caption font-weight-bold">REF</th>
                    <th class="tenant-col caption font-weight-bold">PROPERTY - UNIT - TENANT</th>
                    <th v-for="month in months" :key="month" class="amount-col caption font-weight-bold">
                      {{ month }}
                    </th>
                    <th class="total-col caption font-weight-bold">TOTAL</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in filteredRows" :key="row.reference">
                    <td class="reference-col caption">{{ row.reference }}</td>
                    <td class="tenant-col caption">
                      {{ row.property }} - Unit {{ row.unit || '—' }} - {{ row.tenantName || 'Unknown' }}
                    </td>
                    <td v-for="month in months" :key="month" class="amount-col caption text-right">
                      {{ formatCurrency(row.monthly[month] || 0) }}
                    </td>
                    <td class="total-col caption text-right font-weight-bold">
                      {{ formatCurrency(row.total) }}
                    </td>
                  </tr>
                  <tr class="grand-total">
                    <td colspan="2" class="text-left pl-4 caption font-weight-bold">TOTAL BANKED</td>
                    <td v-for="month in months" :key="month" class="amount-col caption text-right font-weight-bold">
                      {{ formatCurrency(monthlyTotals[month]) }}
                    </td>
                    <td class="total-col caption text-right font-weight-bold">
                      {{ formatCurrency(grandTotal) }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </template>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import { collection, getDocs, query, where, orderBy } from "firebase/firestore";
import { getFirestore } from "firebase/firestore";
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const db = getFirestore();

export default {
  name: 'BankReceivedReport',
  props: {
    property: Object,
    properties: { type: Array, default: () => [] },
    unit: Object,
    landlord: Object
  },
  data: () => ({
    startDate: null,
    endDate: null,
    startMenu: false,
    endMenu: false,
    loading: false,
    pdfLoading: false,
    rows: [],
    selectedUnit: null,
    sortMode: 'unit',
    base64logo: '',
    months: [],
    isLandlordMode: false,
    landlordName: '',
    propertyName: ''
  }),

  computed: {
    unitItems() {
      const units = [...new Set(this.rows.map(r => r.unit).filter(Boolean))];
      return [
        { text: 'All Units', value: null },
        ...units.sort((a,b) => Number(a) - Number(b)).map(u => ({ text: `Unit ${u}`, value: u }))
      ];
    },
    filteredRows() {
      let result = [...this.rows];
      if (this.selectedUnit) {
        result = result.filter(r => r.unit === this.selectedUnit);
      }
      if (this.sortMode === 'unit') {
        result.sort((a,b) => (Number(a.unit || 999999) - Number(b.unit || 999999)));
      } else {
        result.sort((a,b) => a.reference.localeCompare(b.reference));
      }
      return result;
    },
    monthlyTotals() {
      const totals = {};
      this.months.forEach(m => {
        totals[m] = this.filteredRows.reduce((sum, r) => sum + (r.monthly[m] || 0), 0);
      });
      return totals;
    },
    grandTotal() {
      return Object.values(this.monthlyTotals).reduce((a, b) => a + b, 0);
    }
  },

  async created() {
    const today = new Date();
    this.endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().split('T')[0];
    this.startDate = new Date(today.getFullYear(), today.getMonth() - 5, 1).toISOString().split('T')[0];
    await this.fetchLogo();
  },

  methods: {
    async fetchLogo() {
      try {
        const q = query(collection(db, "Logo"), where("id", "==", 2));
        const snap = await getDocs(q);
        if (!snap.empty) {
          const data = snap.docs[0].data();
          this.base64logo = (data.logo2 || data.logo || '').replace(/^data:image\/[a-z]+;base64,/, '');
        }
      } catch (e) {
        console.warn("Logo fetch failed", e);
      }
    },

    async loadData() {
      this.loading = true;
      this.rows = [];
      this.months = [];

      try {
        const props = this.properties.length ? this.properties : this.property ? [this.property] : [];
        if (!props.length) return;

        this.isLandlordMode = this.properties.length > 1 || (this.properties.length === 1 && this.landlord);
        this.landlordName = this.landlord?.name || '';
        this.propertyName = this.isLandlordMode ? '' : (props[0]?.displayName || 'Unknown Property');

        const start = new Date(this.startDate);
        const end = new Date(this.endDate);
        end.setHours(23, 59, 59, 999);

        const monthSet = new Set();

        for (const p of props) {
          let leaseQ = query(collection(db, "Leases"), where("propertyId", "==", p.id));
          if (this.unit?.unitName) {
            leaseQ = query(leaseQ, where("address", "==", this.unit.unitName));
          }

          const leasesSnap = await getDocs(leaseQ);

          for (const leaseDoc of leasesSnap.docs) {
            const lease = leaseDoc.data();
            const ref = lease.leaseReference || lease.reference || lease.leaseRef || lease.ref || 'UNKNOWN';
            const tenant = lease.tenantName || lease.tenant || 'Unknown';
            const unit = lease.address?.includes('Unit') 
              ? lease.address.split('Unit')[1]?.trim() 
              : lease.unit || lease.unitNumber || null;

            const txQ = query(
              collection(db, "Transactions"),
              where("leaseReference", "==", ref),
              where("type", "==", "payment"),
              orderBy("datePaid", "asc")
            );

            const txSnap = await getDocs(txQ);
            const monthly = {};

            txSnap.forEach(doc => {
              const t = doc.data();

              let date;
              if (t.datePaid?.seconds) {
                date = new Date(t.datePaid.seconds * 1000);
              } else if (typeof t.datePaid === 'string') {
                date = new Date(t.datePaid);
              } else {
                return;
              }

              if (isNaN(date.getTime()) || date < start || date > end) return;

              const rawAmount = Number(t.amountPaid || 0);
              if (Math.abs(rawAmount) < 0.01) return;

              const amount = Math.abs(rawAmount);

              const monthKey = date.toLocaleString('default', { month: 'long', year: 'numeric' });
              monthSet.add(monthKey);

              monthly[monthKey] = (monthly[monthKey] || 0) + amount;
            });

            const total = Object.values(monthly).reduce((a, b) => a + b, 0);
            if (total > 0) {
              this.rows.push({
                reference: ref,
                property: p.displayName || 'Property',
                unit,
                tenantName: tenant,
                monthly,
                total
              });
            }
          }
        }

        this.months = [...monthSet].sort((a, b) => new Date(a) - new Date(b));
      } catch (err) {
        console.error("Load data error:", err);
      } finally {
        this.loading = false;
      }
    },

    formatCurrency(val) {
      if (!val || isNaN(val) || val === 0) return '—';
      return 'R ' + Number(val).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },

    formatDate(date) {
      return date ? new Date(date).toLocaleDateString('en-ZA', { year: 'numeric', month: 'short', day: 'numeric' }) : '';
    },

    async exportPdf() {
      if (!this.filteredRows.length) return;

      this.pdfLoading = true;

      try {
        const doc = new jsPDF({
          orientation: 'landscape',
          unit: 'pt',
          format: 'a4'
        });

        const pageWidth = doc.internal.pageSize.getWidth();
        const margin = 40;
        let y = 35;

        // ── Compact Header with centered logo ───────────────────────────────
        if (this.base64logo) {
          try {
            const logoSrc = this.base64logo.startsWith('data:')
              ? this.base64logo
              : `data:image/png;base64,${this.base64logo}`;

            const logoWidth = 80;
            const logoHeight = 25;
            const logoX = (pageWidth - logoWidth) / 2;

            doc.addImage(logoSrc, 'PNG', logoX, y, logoWidth, logoHeight);
            y += logoHeight + 15;
          } catch (e) {
            console.warn('Logo failed in PDF', e);
            y += 30;
          }
        }

        // Title
        let headerTitle = "Bank Received Report";
        if (this.isLandlordMode) {
          headerTitle += " - Multiple Properties";
        } else if (this.propertyName) {
          headerTitle += ` - ${this.propertyName}`;
        }

        doc.setFontSize(16);
        doc.setTextColor(60, 60, 60);
        doc.setFont("helvetica", "bold");
        doc.text(headerTitle, pageWidth / 2, y, { align: 'center' });
        y += 22;

        // Period
        doc.setFontSize(8);
        doc.setTextColor(85, 85, 85);
        doc.setFont("helvetica", "normal");
        doc.text(
          `Period: ${this.formatDate(this.startDate)} — ${this.formatDate(this.endDate)}`,
          pageWidth / 2,
          y,
          { align: 'center' }
        );
        y += 18;

        // Separator
        doc.setLineWidth(0.7);
        doc.setDrawColor(180, 180, 180);
        doc.line(margin, y, pageWidth - margin, y);
        y += 20;

        // ── Table ───────────────────────────────────────────────────────────
        const columns = [
          { header: 'REF', dataKey: 'reference' },
          { header: 'PROPERTY - UNIT - TENANT', dataKey: 'tenantDesc' },
          ...this.months.map(m => ({ header: m, dataKey: m })),
          { header: 'TOTAL', dataKey: 'total' }
        ];

        const body = this.filteredRows.map(r => {
          const row = {
            reference: r.reference || '',
            tenantDesc: `${r.property} - Unit ${r.unit || '—'} - ${r.tenantName || 'Unknown'}`
          };
          this.months.forEach(m => {
            row[m] = this.formatCurrency(r.monthly[m] || 0);
          });
          row.total = this.formatCurrency(r.total);
          return row;
        });

        const totalsRow = {
          reference: 'TOTAL',
          tenantDesc: ''
        };
        this.months.forEach(m => {
          totalsRow[m] = this.formatCurrency(this.monthlyTotals[m] || 0);
        });
        totalsRow.total = this.formatCurrency(this.grandTotal);
        body.push(totalsRow);

        autoTable(doc, {
          columns,
          body,
          startY: y,
          theme: 'grid',
          margin: { left: margin, right: margin },
          styles: {
            fontSize: 8,
            cellPadding: 5,
            lineColor: [180, 180, 180],
            lineWidth: 0.3,
            overflow: 'linebreak'
          },
          headStyles: {
            fillColor: [240, 240, 240],
            textColor: [0, 0, 0],
            fontStyle: 'bold',
            halign: 'center',
            fontSize: 7.5
          },
          alternateRowStyles: { fillColor: [250, 250, 250] },
          columnStyles: {
            reference: { cellWidth: 60, halign: 'left' },
            tenantDesc: { cellWidth: 180, halign: 'left' },
            total: { cellWidth: 70, fontStyle: 'bold' }
          },
          didParseCell: (data) => {
            if (data.column.dataKey !== 'reference' && data.column.dataKey !== 'tenantDesc') {
              data.cell.styles.halign = 'right';
            }
            if (data.row.index === body.length - 1) {
              data.cell.styles.fillColor = [210, 230, 245];
              data.cell.styles.fontStyle = 'bold';
            }
          },
          didDrawPage: () => {
            doc.setFontSize(6);
            doc.setTextColor(120);
            doc.text(
              `Page ${doc.internal.getNumberOfPages()} • ${new Date().toLocaleDateString('en-ZA')}`,
              pageWidth - margin - 10,
              doc.internal.pageSize.height - 15,
              { align: 'right' }
            );
          }
        });

        const periodStr = `${this.formatDate(this.startDate).replace(/\//g, '-')}_to_${this.formatDate(this.endDate).replace(/\//g, '-')}`;
        const safeName = this.propertyName ? this.propertyName.replace(/[^a-zA-Z0-9]/g, '_') : 'BankReceived';
        doc.save(`Bank_Received_${safeName}_${periodStr}.pdf`);

      } catch (err) {
        console.error('PDF generation failed:', err);
        alert('Failed to generate PDF. Please check the console for details.');
      } finally {
        this.pdfLoading = false;
      }
    }
  }
};
</script>

<style scoped>
/* Compact Card */
.compact-card {
  border-radius: 12px !important;
}

/* Bank Table */
.bank-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.7rem !important;
  table-layout: auto;
}

.bank-table th,
.bank-table td {
  border: 1px solid #ddd;
  padding: 5px 8px !important;
  font-size: 0.7rem !important;
  color: #000000 !important;
}

.bank-table th {
  background-color: #f0f0f0 !important;
  font-weight: 700 !important;
  color: #000000 !important;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  font-size: 0.6rem !important;
  border-bottom: 2px solid #ccc !important;
}

.bank-table .grand-total {
  background-color: #e3f2fd !important;
  font-weight: bold !important;
}

.bank-table .grand-total td {
  border-top: 2px solid #999 !important;
  font-weight: 700 !important;
}

.table-responsive {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.reference-col { 
  width: 80px; 
  min-width: 80px;
}
.tenant-col { 
  min-width: 280px; 
}
.amount-col { 
  width: 85px; 
  min-width: 85px;
  text-align: right; 
}
.total-col { 
  width: 95px; 
  min-width: 95px;
  text-align: right; 
  font-weight: bold; 
}

/* Small text field */
.small-text-field :deep(.v-input__slot) {
  min-height: 36px !important;
}

.small-text-field :deep(.v-label) {
  font-size: 0.7rem !important;
  color: #000000 !important;
}

.small-text-field :deep(.v-input__control input) {
  font-size: 0.7rem !important;
  color: #000000 !important;
}

/* Small select */
.small-select :deep(.v-input__slot) {
  min-height: 36px !important;
}

.small-select :deep(.v-label) {
  font-size: 0.7rem !important;
  color: #000000 !important;
}

.small-select :deep(.v-select__selection) {
  font-size: 0.7rem !important;
}

/* Caption class */
.caption {
  font-size: 0.7rem !important;
}

h5 {
  font-size: 0.9rem !important;
  color: #000000 !important;
}

/* Buttons */
.v-btn.caption {
  font-size: 0.65rem !important;
  letter-spacing: 0.3px;
  min-height: 30px !important;
  padding: 0 12px !important;
  text-transform: none !important;
}

.v-btn .v-icon {
  font-size: 16px !important;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .bank-table th,
  .bank-table td {
    font-size: 0.55rem !important;
    padding: 3px 4px !important;
  }
  
  .bank-table th {
    font-size: 0.5rem !important;
  }
  
  .reference-col { 
    width: 60px; 
    min-width: 60px;
  }
  .tenant-col { 
    min-width: 180px; 
  }
  .amount-col { 
    width: 60px; 
    min-width: 60px;
  }
  .total-col { 
    width: 70px; 
    min-width: 70px;
  }
}

@media print {
  .v-btn, .v-btn-toggle, .v-select, .v-expansion-panel {
    display: none !important;
  }
}
</style>